import React from "react"
import { ArrowSubmitButton, ReimbursementDialogFraming, BackArrow } from "../shared/layouts"
import { Progress } from "#/components/views/modal/Progress"
import { useStateMachine } from "#/components/context/stateMachine/StateMachineV2"
import { FormattedMessage, useIntl } from "react-intl"
import { useReimbursementContext } from "#/components/context/reimbursement/GetReimbursedContext"
import { FormRadioButtonGroup } from "#/services/common-forms"
import { DialogActionsBar, Form } from "@carrotfertility/carotene-core-x"
import {
  Box,
  DialogContentText,
  DialogTitle,
  ListItemText,
  UnorderedList,
  UnorderedListItem
} from "@carrotfertility/carotene-core"
import useBenefit from "#/components/dynamic-content/hooks/useGetBenefit"
import { JourneyTypes } from "./ExpenseJourneyTypes"

interface IncludesDonorExpensesFormValues {
  includesDonorExpenses: string
}

export const IncludesDonorExpenses = (): JSX.Element => {
  const { send, back } = useStateMachine()
  const { updateIntendedCarrier, state } = useReimbursementContext()
  const { data: benefit } = useBenefit()
  const intl = useIntl()

  const isCarrotRxRequired = Boolean(benefit?.carrotRx?.isCarrotRxRequired)
  const isRxCovered = Boolean(benefit?.journeys?.fertilityCare?.eligibleExpensesDetail?.isRxCovered)
  const isFertilityCareExpense = state.expenseRelatedTo?.value === JourneyTypes.FERTILITY_CARE
  const shouldRouteToMedication = isFertilityCareExpense && isCarrotRxRequired && isRxCovered

  const onSubmit = (values: IncludesDonorExpensesFormValues): void => {
    if (values.includesDonorExpenses === "true") {
      send("YES")
    } else {
      updateIntendedCarrier(null)
      send(shouldRouteToMedication ? "RELATED_TO_MEDICATION" : "")
    }
  }

  return (
    <Form<IncludesDonorExpensesFormValues> onSubmit={onSubmit}>
      <Progress
        numSteps={3}
        step={2}
        label={intl.formatMessage({ defaultMessage: "About This Expense" })}
        inactiveStepBackgroundColor="#DBE6EE"
      />
      <BackArrow onClick={back} />
      <ReimbursementDialogFraming>
        <DialogTitle id="step-heading">
          <FormattedMessage defaultMessage="Does this include any of the following donor expenses?" />
        </DialogTitle>
        <Box sx={{ display: "flex", flexDirection: "column", gap: (theme) => theme.spacing(theme.tokens.spacing.sm) }}>
          <DialogContentText>
            <FormattedMessage defaultMessage="This helps us determine if the care may be a qualified medical expense." />
          </DialogContentText>
          <UnorderedList disablePadding sx={{ marginBlock: 0 }}>
            {[
              intl.formatMessage({ defaultMessage: "Purchase of donor eggs or sperm" }),
              intl.formatMessage({ defaultMessage: "Legal or agency fees related to donor assistance" }),
              intl.formatMessage({ defaultMessage: "Cryobank or transportation fees for donor materials" }),
              intl.formatMessage({ defaultMessage: "Screening, egg retrieval, or other costs for a donor" })
            ].map((item) => (
              <UnorderedListItem disablePadding key={item}>
                <ListItemText primary={item} />
              </UnorderedListItem>
            ))}
          </UnorderedList>
          <FormRadioButtonGroup
            aria-labelledby="step-heading"
            name="includesDonorExpenses"
            controllerProps={{ rules: { required: intl.formatMessage({ defaultMessage: "Required" }) } }}
            options={[
              { label: intl.formatMessage({ defaultMessage: "Yes" }), value: "true" },
              { label: intl.formatMessage({ defaultMessage: "No" }), value: "false" }
            ]}
          />
        </Box>
      </ReimbursementDialogFraming>
      <DialogActionsBar
        primaryAction={
          <ArrowSubmitButton>
            <FormattedMessage defaultMessage="Continue" />
          </ArrowSubmitButton>
        }
      />
    </Form>
  )
}
